import React, {useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {BottomSheetPanel} from "../../../components/CustomModel/BottomSheetPanel";
import {t} from "i18next";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";
import {formatLastName, getOrdinalSuffix, buildProfileAction} from "../../../constants";
import ParticipantActionsModal from "../../../components/ParticipantActionsModal";
import { MatchScoreColumns, SetSwitcher } from "../matches/MatchScoreColumns";
import {MatchStatusSelect} from "../matches/MatchStatusSelect";
import {useMatchSync, getNormalizedSets} from "../../../hooks/useMatchSync";
import {MatchDetailsModal} from "../matches/MatchDetailsModal";
import MatchParticipantAvatars from "../../../components/MatchParticipantAvatars";
import useTournamentPlayerImages from "../../../hooks/useTournamentPlayerImages";
import useAxios from "../../../utils/useAxios";

// side is 1 or 2 (matches match.player_1/player_2's own numbering) - grade 2
// pairs it with the _3/_4 doubles partner slot. Team matches (grade 3) never
// get avatars at all (a photo pair doesn't represent a team the way it does
// an individual/pair), so this returns null before even touching `images`.
const getAvatarData = (match, side, grade, images) => {
    if (grade === 3) return null;
    const slots = grade === 2
        ? (side === 1 ? [[match.player_1, match.player_1_name], [match.player_3, match.player_3_name]] : [[match.player_2, match.player_2_name], [match.player_4, match.player_4_name]])
        : (side === 1 ? [[match.player_1, match.player_1_name]] : [[match.player_2, match.player_2_name]]);
    const present = slots.filter(([id]) => id);
    if (!present.length) return null;
    return {
        images: present.map(([id]) => images[id]),
        names: present.map(([, name]) => name ? `${name.first_name || ""} ${name.last_name || ""}`.trim() : "")
    };
};

// p is the participant id (match.player_1/player_2, or team id for grade 3),
// used here only for existence/click-target purposes - the actual name comes
// from match.player_X_name below. onPlayerClick opens that participant's
// profile - unlike the bracket grid itself (Bracket.js/BracketClassic.js),
// where this same click now only opens the match (see their own onClick
// comments), names here are free to be their own dedicated click target
// since there's no competing "open the match" behavior on this row - the
// whole card is already the match, tapping it just selects/scrolls to it.
const getName = (p, match, grade, index, currentIndex, winner_side, row_side, onPlayerClick, onTeamClick) => {
    if ((match.x1 && index === 0) || (match.x2 && index === 1)) return <span className="uppercase0">{t("Bye")}</span>;
    // a bye never gets its own winner_side (there's no match to record a
    // result on) - the side facing it still advances automatically, so it
    // reads as a win too, not the same "no result yet" blank as an
    // actually-unplayed match.
    if (match.x1) winner_side = 2;
    else if (match.x2) winner_side = 1;
    if (!p) {
        const sideSource = index === 1 ? match.source_match_2 : match.source_match_1;
        if (sideSource) {
            const label = match.is_source_winner ? t("Winner") : t("Loser");
            return `${label} ${t("M")}${sideSource}`;
        } else {
            return `${t("Participant")} ${match.match_number * 2 + index - 1}`
        }
    }
    const isSide1 = index === 0;
    const clickProps = (id) => onPlayerClick ? {
        onClick: (e) => { e.stopPropagation(); onPlayerClick(id); },
        style: { cursor: "pointer" }
    } : {};
    if (grade === 1) {
        const p = match[`player_${index + 1}_name`];
        return <div className={`player black-name ${winner_side ? (row_side === winner_side ? "match-winner" : "match-loser") : ""}`} {...clickProps(match[`player_${index + 1}`])}>
            {p?.first_name && <>{p.first_name[0]}. </>}<span className="uppercase0">{p.last_name ? formatLastName(p.last_name) : ""}</span>
        </div>;
    }
    if (grade === 2) {
        const p = isSide1
            ? [[match.player_1_name, match.player_1], [match.player_3_name, match.player_3]].filter(([name]) => name)
            : [[match.player_2_name, match.player_2], [match.player_4_name, match.player_4]].filter(([name]) => name);
        return (
            <div>
                {p.map(([player, id], i) => (
                    <React.Fragment key={i}>
                        <div className={`player black-name ${winner_side ? (row_side === winner_side ? "match-winner" : "match-loser") : ""}`} {...clickProps(id)}>
                            {player.first_name && `${player.first_name[0]}. `}
                            <span className="uppercase0">{formatLastName(player.last_name)}</span>
                            {i < p.length - 1 && <br/>}
                        </div>
                    </React.Fragment>
                ))}
            </div>
        );
    }
    const tName = isSide1 ? match.team_1_name : match.team_2_name;
    const tNumber = isSide1 ? match.team_1_number : match.team_2_number;
    const teamClickProps = (onTeamClick && tName) ? {
        onClick: (e) => { e.stopPropagation(); onTeamClick(tName); },
        style: { cursor: "pointer" }
    } : {};
    return <div className={`player black-name ${winner_side ? (row_side === winner_side ? "match-winner" : "match-loser") : ""}`} {...teamClickProps}>{tName || `${t("Team")} ${tNumber}`}</div>
};

const TournamentGridMatchesModal = ({ isOpen, onClose, draws = [], tournamentId, tournamentGrade, selectedMatchId, setSelectedMatchId, expandSignal, stages, activeStage, updateMatches, is_authorized, searchChips = [], teamRoster, playerNames }) => {
    const [loadingMatches, setLoadingMatches] = useState({});
    const [detailsMatch, setDetailsMatch] = useState(null);
    const [teamMenu, setTeamMenu] = useState(null);
    // Which set (0-4) each match's score inputs/SetSwitcher currently show -
    // keyed by match id, same reasoning as TournamentGroupsMatchesModal.js's
    // own activeSetIndexes.
    const [activeSetIndexes, setActiveSetIndexes] = useState({});
    const history = useHistory();
    const location = useLocation();
    const api = useAxios();
    const playerImages = useTournamentPlayerImages(api, tournamentId, tournamentGrade !== 3);

    const handlePlayerClick = (pId) => {
        const params = new URLSearchParams(location.search);
        params.set("player_id", pId);
        // replace, not push - same reasoning as Bracket.js's own handlePlClick
        // (see its comment): a push here left an extra history entry the
        // tournament header's back-arrow goBack() would undo first.
        history.replace({ search: params.toString() });
    };

    // "Who's on this team" menu - mirrors Bracket.js/BracketClassic.js's own
    // openParticipantMenu, but reads the already-prefetched teamRoster map
    // (see fetchTeamRosterMap.js) instead of fetching on demand, since this
    // list already has it loaded for search-chip matching (matchOf above).
    const openTeamMenu = (teamName) => {
        const roster = teamRoster?.get(teamName) || [];
        setTeamMenu({ title: teamName, actions: roster.map(p => buildProfileAction(p, history, location)) });
    };

    const syncMatchApi = useMatchSync(tournamentId, (res) => {
        if (res?.data?.updated_matches) {
            updateMatches(res.data.updated_matches, false);
            setLoadingMatches(prev => {
                const next = { ...prev };
                res.data.updated_matches.forEach(m => delete next[m.id]);
                return next;
            });
        }
    });

    const saveMatch = async (match, newFields = {}, setIndex = 0) => {
        if (newFields.winner_side !== undefined && newFields.status === undefined) {
            const currentStatus = match?.status;
            if (currentStatus !== 5 && currentStatus !== 6 && currentStatus !== 7) {
                newFields.status = 7;
            }
        }

        if (newFields.status !== undefined) {
            setLoadingMatches(prev => ({ ...prev, [match.id]: true }));
        }

        let isSetUpdate = false;
        const mainFields = {};
        const setFields = {};

        const setFieldNames = ['game_score_1', 'game_score_2', 'tiebreak_score_1', 'tiebreak_score_2', 'point_score_1', 'point_score_2', 'serving_player'];

        Object.keys(newFields).forEach(k => {
            if (setFieldNames.includes(k)) {
                setFields[k] = newFields[k];
                isSetUpdate = true;
            } else {
                mainFields[k] = newFields[k];
            }
        });

        const currentSets = getNormalizedSets(match?.sets);
        // Note: draws modal doesn't have a local 'scores' state like groups, it uses updateMatches directly
        const newSets = [...currentSets];
        for (let i = 0; i <= setIndex; i++) {
            if (!newSets[i]) newSets[i] = { number: i + 1 };
        }

        if (isSetUpdate) {
            newSets[setIndex] = { ...newSets[setIndex], ...setFields, number: setIndex + 1 };
        }

        updateMatches([{ id: match.id, ...mainFields, sets: newSets }], false);
        let payloadToSync;
        if (isSetUpdate) {
            payloadToSync = { sets: [{ ...setFields, number: setIndex + 1 }] };
        } else {
            payloadToSync = { ...newFields };
        }
        syncMatchApi(match.id, payloadToSync);
    };

    const getRoundShort = (round, totalRounds) => {
        if (round === totalRounds) return t("Final");
        if (round === totalRounds - 1) return "SF";
        if (round === totalRounds - 2) return "QF";
        return `R${round}`;
    };

    // Whole match card highlighted together if either side matches - same
    // "test both, not just the one that happens to match" idea as
    // Bracket.js/BracketClassic.js's own renderSide.
    // match.player_X_name only carries a single-letter first_name (display-only
    // backend quirk - see fetchPlayerNameMap.js's own comment); resolve the
    // full name from the prefetched playerNames map so a full-first-name
    // search chip still matches.
    const resolvePlayer = (p, pid) => {
        const key = pid || p?.id;
        return (p && key) ? (playerNames?.get(key) || p) : p;
    };

    const matchOf = (match, draw) => {
        const names = tournamentGrade === 3
            ? [match.team_1_name, match.team_2_name].filter(Boolean).flatMap(name => [name, ...(teamRoster?.get(name) || [])])
            : [
                resolvePlayer(match.player_1_name, match.player_1), resolvePlayer(match.player_2_name, match.player_2),
                resolvePlayer(match.player_3_name, match.player_3), resolvePlayer(match.player_4_name, match.player_4)
            ].filter(Boolean);
        return chipsMatchEntity(searchChips, { names, drawValue: draw.number });
    };

    // Hoisted out of the per-draw loop below (was recomputed per-draw for no
    // draw-specific reason - every draw in this modal shares the same
    // activeStage) so MatchDetailsModal, rendered once outside that loop, can
    // use the same value for whichever match is currently selected.
    const currentStage = stages?.find(s => s.id === activeStage);
    const isMultiSet = currentStage?.games_in_set === 6;

    let globalMatchIndex = 0;
    return (
        <BottomSheetPanel isOpen={isOpen} onClose={onClose} expandSignal={expandSignal} scrollTargetId={selectedMatchId != null ? `match-${selectedMatchId}` : null} title={t("Matches")} icon="list">
            <form className="modal-form">
                <div className="team-table matches">
                    {draws.map(draw => {
                        // draw.matches itself comes in round_number ascending order (round 1
                        // first, final last - see totalRounds below, which still reads the
                        // untouched draw.matches) - reversed here so the final sits at the
                        // top of the list instead of requiring a scroll to the bottom. Order
                        // within a round stays its normal top-to-bottom match_order, only the
                        // round grouping itself flips.
                        const sortedMatches = [...(draw.matches || [])]
                            .sort((a, b) => b.round_number - a.round_number || (a.match_order ?? a.match_number) - (b.match_order ?? b.match_number));

                        if (!sortedMatches.length) return null;

                        return (
                            <React.Fragment key={draw.id}>
                                <label style={{marginTop: 0}}>{t("Bracket of")} {draw.number} {t("place")}</label>
                                {sortedMatches.map(match => {
                                    if (match?.x1 || match?.x2) {
                                        return
                                    }
                                    const currentIndex = globalMatchIndex++;
                                    const matched = matchOf(match, draw);
                                    const matchSets = getNormalizedSets(match?.sets);
                                    const optimisticVals = matchSets[0] || {};
                                    const totalRounds = draw.matches?.at(-1)?.round_number || 1;

                                    const rows = [
                                        { side: 1, player: tournamentGrade === 3 ? match.team_1 : match.player_1, games: optimisticVals.game_score_1, tb: optimisticVals.tiebreak_score_1, index: 0 },
                                        { side: 2, player: tournamentGrade === 3 ? match.team_2 : match.player_2, games: optimisticVals.game_score_2, tb: optimisticVals.tiebreak_score_2, index: 1 }
                                    ];
                                    const canEdit = is_authorized && rows.every(r => r.player);
                                    const activeSetIndex = activeSetIndexes[match.id] || 0;
                                    const setActiveSetIndex = (updater) => setActiveSetIndexes(prev => ({
                                        ...prev,
                                        [match.id]: typeof updater === "function" ? updater(prev[match.id] || 0) : updater
                                    }));

                                    return (
                                        <div id={`match-${match.match_number}`} key={match.id} onClick={() => setSelectedMatchId(match.match_number)} className={`team-card ${String(match.match_number) === String(selectedMatchId) ? "selected" : (canEdit ? "editable" : "")} ${searchStateClass(matched)}`} style={{ marginBottom: "0" }}>
                                            <div className="team-name-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                                                {draw.number}{getOrdinalSuffix(draw.number)} {t("Place")} · {getRoundShort(match.round_number, totalRounds)}
                                                    {match.round_number !== totalRounds && `-${match.match_order}`}
                                                    {match.match_number && <> · #{match.match_number}</>}
                                                </span>
                                                {is_authorized && <SetSwitcher activeSetIndex={activeSetIndex} setActiveSetIndex={setActiveSetIndex} />}
                                            </div>

                                            <div>
                                                {rows.map((row, i) => (
                                                    <div key={i} className="team-player-row">
                                                        <MatchParticipantAvatars {...getAvatarData(match, row.side, tournamentGrade, playerImages) || {}} grade={tournamentGrade} />
                                                        {getName(row.player, match, tournamentGrade, i, currentIndex, match.winner_side, row.side, handlePlayerClick, openTeamMenu)}
                                                    </div>
                                                ))}
                                            </div>

                                            <MatchScoreColumns
                                                matchId={match.id}
                                                winnerSide={match.winner_side}
                                                rows={rows}
                                                canEdit={canEdit}
                                                is_authorized={is_authorized}
                                                matchSets={matchSets}
                                                activeSetIndex={activeSetIndex}
                                                onSave={(newFields, setIndex) => saveMatch(match, newFields, setIndex)}
                                            />

                                            <div className="team-name-row" style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", marginBottom: "0", position: "relative"}}>
                                                {match?.start_time && <span>{match.start_time} · {t("Court")} {match.court}</span>}
                                                {match && !match.x1 && !match.x2 &&
                                                    <MatchStatusSelect
                                                        match={match}
                                                        is_authorized={is_authorized}
                                                        onSave={(payload) => saveMatch(match, payload)}
                                                        min_height={true}
                                                        loader={loadingMatches[match.id]}
                                                    />
                                                }
                                            </div>
                                            {/* Own row, not squeezed next to MatchStatusSelect above -
                                                that select's own wrapper (.matches-status-select,
                                                index.css:8042) is `position: absolute; right: 0;
                                                width: 140px`, floating out of flow and painting on top
                                                of anything else placed in that same row (confirmed live:
                                                a "Details" button put there as a flex sibling silently
                                                ate every click, always reopening the status dropdown
                                                instead). Team matches (grade 3) need this to reach their
                                                own sub-matches breakdown (SubMatchesPanel, via
                                                MatchDetailsModal below) - grade 1/2 already have
                                                everything editable inline via MatchScoreColumns above and
                                                don't need a second entry point. */}
                                            {tournamentGrade === 3 && match && !match.x1 && !match.x2 && (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                                                    <button
                                                        type="button"
                                                        className="action-button btn-green"
                                                        style={{ padding: "2px 10px", minHeight: "28px" }}
                                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDetailsMatch(match); }}
                                                    >
                                                        {t("Details")}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>
            </form>
            <MatchDetailsModal
                isOpen={!!detailsMatch}
                onClose={() => setDetailsMatch(null)}
                match={detailsMatch}
                tournamentGrade={tournamentGrade}
                tournamentId={tournamentId}
                is_authorized={is_authorized}
                isMultiSet={isMultiSet}
                onSave={(payload, setIndex) => saveMatch(detailsMatch, payload, setIndex)}
            />
            <ParticipantActionsModal
                isOpen={!!teamMenu}
                onClose={() => setTeamMenu(null)}
                title={teamMenu?.title}
                actions={teamMenu?.actions}
            />
        </BottomSheetPanel>
    );
};

export default TournamentGridMatchesModal;
